import React, { useState, useCallback, useRef } from 'react';
import {
  FolderOpen,
  ListChecks,
  Layers,
  Wrench,
  ScrollText,
  Bug,
  BarChart3,
  X,
} from 'lucide-react';

export type PanelId =
  | 'files'
  | 'plan'
  | 'canvas'
  | 'tools'
  | 'prompt'
  | 'debug'
  | 'usage';

interface TabDef {
  id: PanelId;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  badge?: number;
  hidden?: boolean;
}

interface RightPanelProps {
  activeTab: PanelId | null;
  onTabChange: (tab: PanelId | null) => void;
  /** Badge counts / visibility flags */
  toolsBadge?: number;
  canvasHidden?: boolean;
  promptHidden?: boolean;
  /** Panel content — the existing sidebar components passed as children */
  children: React.ReactNode;
}

const MIN_WIDTH = 280;
const MAX_WIDTH = 700;
const DEFAULT_WIDTH = 360;

export const RightPanel = React.memo(function RightPanel({
  activeTab,
  onTabChange,
  toolsBadge = 0,
  canvasHidden = true,
  promptHidden = false,
  children,
}: RightPanelProps) {
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = width;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      const delta = startX.current - ev.clientX;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth.current + delta));
      setWidth(newWidth);
    };
    const onMouseUp = () => {
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [width]);

  if (activeTab === null) return null;

  const tabs: TabDef[] = [
    { id: 'files', icon: FolderOpen, label: 'Files' },
    { id: 'plan', icon: ListChecks, label: 'Plan' },
    { id: 'canvas', icon: Layers, label: 'Canvas', hidden: canvasHidden },
    {
      id: 'tools',
      icon: Wrench,
      label: 'Tools',
      badge: toolsBadge || undefined,
    },
    { id: 'prompt', icon: ScrollText, label: 'Prompt', hidden: promptHidden },
    { id: 'debug', icon: Bug, label: 'Debug' },
    { id: 'usage', icon: BarChart3, label: 'Usage' },
  ];

  const visibleTabs = tabs.filter((t) => !t.hidden);
  const activeTabDef = visibleTabs.find((t) => t.id === activeTab);

  return (
    <div
      className="flex flex-col h-full border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-[-2px_0_8px_rgba(0,0,0,0.04)] flex-shrink-0 relative"
      style={{ width: `${width}px` }}
    >
      {/* Resize handle on the left edge */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-sky-400 transition-colors z-10"
        onMouseDown={onMouseDown}
      />

      {/* Panel header: tab strip + close */}
      <div className="flex items-center border-b border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-800/70 flex-shrink-0">
        <div className="flex items-center overflow-x-auto no-scrollbar flex-1">
          {visibleTabs.map((tab) => {
            const isActive = tab.id === activeTab;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(isActive ? null : tab.id)}
                title={tab.label}
                className={`relative flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors flex-shrink-0 ${
                  isActive
                    ? 'border-sky-500 text-sky-600 bg-white dark:bg-gray-900'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                data-testid={`panel-tab-${tab.id}`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{tab.label}</span>
                {tab.badge != null && tab.badge > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-violet-600 text-white text-[10px] rounded-full w-3.5 h-3.5 flex items-center justify-center leading-none">
                    {tab.badge > 9 ? '9+' : tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Close panel entirely */}
        <button
          onClick={() => onTabChange(null)}
          className="flex-shrink-0 p-2 mr-1 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title={`Close ${activeTabDef?.label ?? 'panel'}`}
          data-testid="close-right-panel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Panel content — force children to fill width, suppress their own resize handles */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0 [&>*]:!w-full [&>*]:!border-l-0 [&>*_.cursor-col-resize]:!hidden">
        {children}
      </div>
    </div>
  );
});
